import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tabs,
  Tab,
  Box as TabPanel
} from '@mui/material';
import {
  History,
  Security,
  FilterList,
  Refresh,
  ExpandMore,
  Download
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import emergencyAPI from '../../services/emergencyAPI';
import type { EmergencyLog, SecurityIncident } from '../../types/emergency';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanelComponent(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`emergency-tabpanel-${index}`}
      aria-labelledby={`emergency-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const EmergencyLogs: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [logs, setLogs] = useState<EmergencyLog[]>([]);
  const [incidents, setIncidents] = useState<SecurityIncident[]>([]);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [incidentsLoading, setIncidentsLoading] = useState(false);

  // Pagination
  const [logsPage, setLogsPage] = useState(0);
  const [logsRowsPerPage, setLogsRowsPerPage] = useState(25);
  const [logsTotalCount, setLogsTotalCount] = useState(0);
  const [incidentsPage, setIncidentsPage] = useState(0);
  const [incidentsRowsPerPage, setIncidentsRowsPerPage] = useState(25);
  const [incidentsTotalCount, setIncidentsTotalCount] = useState(0);

  // Filters
  const [logsAction, setLogsAction] = useState('');
  const [logsDateFrom, setLogsDateFrom] = useState<Dayjs | null>(dayjs().subtract(7, 'days'));
  const [logsDateTo, setLogsDateTo] = useState<Dayjs | null>(dayjs());
  const [incidentsSeverity, setIncidentsSeverity] = useState('');
  const [incidentsResolved, setIncidentsResolved] = useState<boolean | ''>('');
  const [incidentsDateFrom, setIncidentsDateFrom] = useState<Dayjs | null>(dayjs().subtract(7, 'days'));
  const [incidentsDateTo, setIncidentsDateTo] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    fetchLogs();
    fetchIncidents();
  }, []);

  const fetchLogs = async () => {
    setLogsLoading(true);
    try {
      const params = {
        page: logsPage + 1, // API is 1-indexed
        limit: logsRowsPerPage,
        action: logsAction || undefined,
        date_from: logsDateFrom?.format('YYYY-MM-DD'),
        date_to: logsDateTo?.format('YYYY-MM-DD')
      };

      const response = await emergencyAPI.getLogs(params);
      setLogs(response.logs || []);
      setLogsTotalCount(response.pagination?.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch emergency logs:', error);
      toast.error('Failed to load emergency logs');
      setLogs([]);
      setLogsTotalCount(0);
    } finally {
      setLogsLoading(false);
      setLoading(false);
    }
  };

  const fetchIncidents = async () => {
    setIncidentsLoading(true);
    try {
      const params = {
        page: incidentsPage + 1, // API is 1-indexed
        limit: incidentsRowsPerPage,
        severity: incidentsSeverity || undefined,
        resolved: incidentsResolved !== '' ? incidentsResolved : undefined,
        date_from: incidentsDateFrom?.format('YYYY-MM-DD'),
        date_to: incidentsDateTo?.format('YYYY-MM-DD')
      };

      const response = await emergencyAPI.getIncidents(params);
      setIncidents(response.incidents || []);
      setIncidentsTotalCount(response.pagination?.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch security incidents:', error);
      toast.error('Security incidents endpoint not available');
      setIncidents([]);
      setIncidentsTotalCount(0);
    } finally {
      setIncidentsLoading(false);
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleLogsPageChange = (event: unknown, newPage: number) => {
    setLogsPage(newPage);
    // Trigger refetch with new page
    setTimeout(fetchLogs, 100);
  };

  const handleIncidentsPageChange = (event: unknown, newPage: number) => {
    setIncidentsPage(newPage);
    // Trigger refetch with new page
    setTimeout(fetchIncidents, 100);
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'info';
      case 'medium': return 'warning';
      case 'high': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Emergency Logs & Incidents</Typography>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              fetchLogs();
              fetchIncidents();
            }}
          >
            Refresh
          </Button>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <History sx={{ mr: 1 }} />
                  Emergency Logs ({logsTotalCount})
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Security sx={{ mr: 1 }} />
                  Security Incidents ({incidentsTotalCount})
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Emergency Logs Tab */}
        <TabPanelComponent value={currentTab} index={0}>
          {/* Logs Filters */}
          <Accordion sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterList sx={{ mr: 1 }} />
                <Typography>Filter Emergency Logs</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Action"
                    value={logsAction}
                    onChange={(e) => setLogsAction(e.target.value)}
                    placeholder="e.g., block_all_api"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="From Date"
                    value={logsDateFrom}
                    onChange={(newValue) => setLogsDateFrom(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="To Date"
                    value={logsDateTo}
                    onChange={(newValue) => setLogsDateTo(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchLogs}
                    disabled={logsLoading}
                    sx={{ height: '56px' }}
                  >
                    Apply Filters
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Logs Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Affected Resource</TableCell>
                    <TableCell>IP Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : logs && logs.length > 0 ? (
                    logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{formatDateTime(log.created_at)}</TableCell>
                        <TableCell>
                          <Chip label={log.action} size="small" />
                        </TableCell>
                        <TableCell>{log.admin_email}</TableCell>
                        <TableCell>{log.reason}</TableCell>
                        <TableCell>{log.affected_resource || '-'}</TableCell>
                        <TableCell>{log.ip_address}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No emergency logs found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={logsTotalCount}
              rowsPerPage={logsRowsPerPage}
              page={logsPage}
              onPageChange={handleLogsPageChange}
              onRowsPerPageChange={(event) => {
                setLogsRowsPerPage(parseInt(event.target.value, 10));
                setLogsPage(0);
                setTimeout(fetchLogs, 100);
              }}
            />
          </Paper>
        </TabPanelComponent>

        {/* Security Incidents Tab */}
        <TabPanelComponent value={currentTab} index={1}>
          {/* Incidents Filters */}
          <Accordion sx={{ mb: 3 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterList sx={{ mr: 1 }} />
                <Typography>Filter Security Incidents</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Severity</InputLabel>
                    <Select
                      value={incidentsSeverity}
                      label="Severity"
                      onChange={(e) => setIncidentsSeverity(e.target.value)}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="critical">Critical</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={incidentsResolved}
                      label="Status"
                      onChange={(e) => setIncidentsResolved(e.target.value as boolean | '')}
                    >
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value={'false' as any}>Open</MenuItem>
                      <MenuItem value={'true' as any}>Resolved</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="From Date"
                    value={incidentsDateFrom}
                    onChange={(newValue) => setIncidentsDateFrom(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="To Date"
                    value={incidentsDateTo}
                    onChange={(newValue) => setIncidentsDateTo(newValue)}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchIncidents}
                    disabled={incidentsLoading}
                    sx={{ height: '56px' }}
                  >
                    Apply Filters
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Incidents Table */}
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Severity</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Source IP</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incidentsLoading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : incidents && incidents.length > 0 ? (
                    incidents.map((incident) => (
                      <TableRow key={incident.id}>
                        <TableCell>{formatDateTime(incident.created_at)}</TableCell>
                        <TableCell>
                          <Chip label={incident.incident_type} size="small" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={incident.severity.toUpperCase()}
                            size="small"
                            color={getSeverityColor(incident.severity) as any}
                          />
                        </TableCell>
                        <TableCell>{incident.description}</TableCell>
                        <TableCell>{incident.source_ip || '-'}</TableCell>
                        <TableCell>
                          <Chip
                            label={incident.is_resolved ? 'Resolved' : 'Open'}
                            size="small"
                            color={incident.is_resolved ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No security incidents found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={incidentsTotalCount}
              rowsPerPage={incidentsRowsPerPage}
              page={incidentsPage}
              onPageChange={handleIncidentsPageChange}
              onRowsPerPageChange={(event) => {
                setIncidentsRowsPerPage(parseInt(event.target.value, 10));
                setIncidentsPage(0);
                setTimeout(fetchIncidents, 100);
              }}
            />
          </Paper>
        </TabPanelComponent>
      </Box>
    </LocalizationProvider>
  );
};

export default EmergencyLogs; 